import {
  Button,
  CloseButton,
  Flex,
  Heading,
  IconLinkLine,
  Modal,
  TextInput,
} from "@instructure/ui";
import {
  $createLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from "@lexical/link";
import { IS_APPLE } from "@lexical/utils";
import { useTranslation } from "react-i18next";
import {
  $createTextNode,
  $getSelection,
  $isRangeSelection,
  KEY_DOWN_COMMAND,
} from "lexical";
import { useEffect, useState } from "react";
import { SHORTCUTS } from "../ShortcutsPlugin";

export const LinkPlugin = ({ editor, isLink }) => {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [open, setOpen] = useState(false);

  // Link Keyboard shortcut
  useEffect(() => {
    editor.registerCommand(
      KEY_DOWN_COMMAND,
      (e) => {
        const modKey = IS_APPLE ? e.metaKey : e.ctrlKey;
        // Link
        if (modKey && e.key === "k") {
          setOpen(true);
          return true;
        }
        return false;
      },
      2
    );
  }, [editor]);

  useEffect(() => {
    if (open) {
      let text = "";

      editor.getEditorState().read(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          text = selection.getTextContent();

          const node = selection.anchor.getNode();
          const parent = node.getParent();

          const linkNode = $isLinkNode(node)
            ? node
            : $isLinkNode(parent)
              ? parent
              : null;

          if (linkNode) {
            setLinkUrl(linkNode.getURL());
            setText(linkNode.getTextContent());
          } else {
            setText(text);
          }
        }
      });
    }
  }, [open, editor]);

  function handleInsert(text, linkUrl) {
    let url = linkUrl;
    if (!/^https?:\/\//.test(url)) {
      url = "https://" + url;
    }

    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      // If user selected text → just apply link
      if (!selection.isCollapsed()) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
          url,
          target: "_blank",
          rel: "noopener noreferrer",
        });
        return;
      }

      // If cursor only → insert linked text
      if (text) {
        const linkNode = $createLinkNode(url, {
          target: "_blank",
          rel: "noopener noreferrer",
        });
        linkNode.append($createTextNode(text));
        selection.insertNodes([linkNode]);
      }
    });
    setOpen(false);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={"toolbar-item spaced " + (isLink ? "active" : "")}
        aria-label={t("rte.link.insertLink")}
        title={`${t("rte.link.insertLink")} (${SHORTCUTS.LINK})`}
      >
        <IconLinkLine size="x-small" />
      </button>
      <Modal
        open={open}
        label={t("rte.link.insertLink")}
        onExited={() => {
          setText("");
          setLinkUrl("");
        }}
        size="medium"
      >
        <Modal.Header>
          <Flex justifyItems="space-between">
            <Flex.Item>
              <Heading as="h3">{t("rte.link.insertLink")}</Heading>
            </Flex.Item>
            <Flex.Item>
              <CloseButton
                offset="small"
                onClick={() => setOpen(false)}
                screenReaderLabel={t("global.close")}
              />
            </Flex.Item>
          </Flex>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <TextInput
              renderLabel={t("rte.link.text")}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <TextInput
              renderLabel={t("rte.link.link")}
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="primary"
            onClick={() => handleInsert(text, linkUrl)}
            disabled={!linkUrl}
          >
            {t("global.submit")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
